import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import API from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  ArrowLeft, MagnifyingGlass, Plus, Upload, Download, User, Key,
  Link as LinkIcon, LinkBreak, Trash, PencilSimple, SpinnerGap,
  CheckCircle, WarningCircle, Cube, CaretRight, FileText, Eye, Ticket
} from '@phosphor-icons/react';

import Breadcrumbs from '../components/Breadcrumbs';

export default function AppAccountsPage() {
  const { appId } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [app, setApp] = useState(null);
  const [stats, setStats] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [uncorrelatedAccounts, setUncorrelatedAccounts] = useState([]);
  const [entitlements, setEntitlements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  
  const [accountSearch, setAccountSearch] = useState('');
  const [uncorrelatedSearch, setUncorrelatedSearch] = useState('');
  const [entitlementSearch, setEntitlementSearch] = useState('');
  
  const [showAccountDialog, setShowAccountDialog] = useState(false);
  const [showEntitlementDialog, setShowEntitlementDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showImportResults, setShowImportResults] = useState(false);
  const [importResults, setImportResults] = useState(null);
  
  const [accountForm, setAccountForm] = useState({ account_id: '', email: '', display_name: '', status: 'active' });
  const [entitlementForm, setEntitlementForm] = useState({ name: '', description: '', type: 'permission' });
  const [editingAccount, setEditingAccount] = useState(null);
  const [editingEntitlement, setEditingEntitlement] = useState(null);
  
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedEntitlement, setSelectedEntitlement] = useState(null);

  const loadApp = useCallback(async () => {
    try {
      const [appRes, statsRes] = await Promise.all([
        API.get(`/applications/${appId}`),
        API.get(`/applications/${appId}/stats`)
      ]);
      setApp(appRes.data);
      setStats(statsRes.data);
    } catch (err) {
      toast.error('Failed to load application');
      navigate('/applications');
    }
  }, [appId, navigate]);

  const loadAccounts = useCallback(async () => {
    try {
      const res = await API.get(`/app-accounts/by-app/${appId}`, {
        params: { search: accountSearch || undefined, limit: 100 }
      });
      setAccounts(res.data.items);
    } catch (err) {
      toast.error('Failed to load accounts');
    }
  }, [appId, accountSearch]);

  const loadUncorrelatedAccounts = useCallback(async () => {
    try {
      const res = await API.get(`/app-accounts/by-app/${appId}`, {
        params: { 
          search: uncorrelatedSearch || undefined, 
          correlation_status: 'uncorrelated',
          limit: 100 
        }
      });
      setUncorrelatedAccounts(res.data.items);
    } catch (err) {
      toast.error('Failed to load uncorrelated accounts');
    }
  }, [appId, uncorrelatedSearch]);

  const loadEntitlements = useCallback(async () => {
    try {
      const res = await API.get(`/app-accounts/entitlements/by-app/${appId}`, {
        params: { search: entitlementSearch || undefined, limit: 100 }
      });
      setEntitlements(res.data.items);
    } catch (err) {
      toast.error('Failed to load entitlements');
    }
  }, [appId, entitlementSearch]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await loadApp();
      await Promise.all([loadAccounts(), loadUncorrelatedAccounts(), loadEntitlements()]);
      setLoading(false);
    };
    init();
  }, [loadApp, loadAccounts, loadUncorrelatedAccounts, loadEntitlements]);

  const handleImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }
    
    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await API.post(`/app-accounts/import/${appId}?create_tickets=true`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setImportResults(res.data.results);
      setShowImportResults(true);
      toast.success('Import completed');
      
      // Reload data
      await loadApp();
      await Promise.all([loadAccounts(), loadUncorrelatedAccounts(), loadEntitlements()]);
      
      // Refresh stats
      const statsRes = await API.get(`/applications/${appId}/stats`);
      setStats(statsRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const downloadTemplate = async () => {
    try {
      const res = await API.get('/app-accounts/import/template');
      const blob = new Blob([res.data.template], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'import_template.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Failed to download template');
    }
  };

  const handleSaveAccount = async () => {
    try {
      if (editingAccount) {
        await API.put(`/app-accounts/${editingAccount}`, {
          display_name: accountForm.display_name,
          status: accountForm.status
        });
        toast.success('Account updated');
      } else {
        await API.post('/app-accounts', {
          application_id: appId,
          ...accountForm
        });
        toast.success('Account created');
      }
      setShowAccountDialog(false);
      setAccountForm({ account_id: '', email: '', display_name: '', status: 'active' });
      setEditingAccount(null);
      loadAccounts();
      
      const statsRes = await API.get(`/applications/${appId}/stats`);
      setStats(statsRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save account');
    }
  };

  const handleSaveEntitlement = async () => {
    try {
      if (editingEntitlement) {
        await API.put(`/app-accounts/entitlements/${editingEntitlement}`, entitlementForm);
        toast.success('Entitlement updated');
      } else {
        await API.post('/app-accounts/entitlements', {
          application_id: appId,
          ...entitlementForm
        });
        toast.success('Entitlement created');
      }
      setShowEntitlementDialog(false);
      setEntitlementForm({ name: '', description: '', type: 'permission' });
      setEditingEntitlement(null);
      loadEntitlements();
      
      const statsRes = await API.get(`/applications/${appId}/stats`);
      setStats(statsRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save entitlement');
    }
  };

  const handleDeleteAccount = async (id) => {
    if (!window.confirm('Delete this account? This will also remove all entitlement assignments.')) return;
    try {
      await API.delete(`/app-accounts/${id}`);
      toast.success('Account deleted');
      loadAccounts();
      
      const statsRes = await API.get(`/applications/${appId}/stats`);
      setStats(statsRes.data);
    } catch (err) {
      toast.error('Failed to delete account');
    }
  };

  const handleDeleteEntitlement = async (id) => {
    if (!window.confirm('Delete this entitlement? This will also remove all account assignments.')) return;
    try {
      await API.delete(`/app-accounts/entitlements/${id}`);
      toast.success('Entitlement deleted');
      loadEntitlements();
      
      const statsRes = await API.get(`/applications/${appId}/stats`);
      setStats(statsRes.data);
    } catch (err) {
      toast.error('Failed to delete entitlement');
    }
  };

  const handleCorrelate = async (accountId) => {
    try {
      const res = await API.post(`/app-accounts/${accountId}/correlate`);
      if (res.data.identity_id) {
        toast.success(`Correlated to ${res.data.identity_name}`);
      } else {
        toast.info('No matching identity found');
      }
      loadAccounts();
      loadUncorrelatedAccounts();
      
      // Refresh stats
      const statsRes = await API.get(`/applications/${appId}/stats`);
      setStats(statsRes.data);
    } catch (err) {
      toast.error('Correlation failed');
    }
  };

  const handleAssignEntitlement = async () => {
    if (!selectedAccount || !selectedEntitlement) return;
    try {
      await API.post('/app-accounts/assign-entitlement?create_ticket=true', {
        account_id: selectedAccount,
        entitlement_id: selectedEntitlement
      });
      toast.success('Entitlement assigned');
      setShowAssignDialog(false);
      setSelectedAccount(null);
      setSelectedEntitlement(null);
      loadAccounts();
      loadEntitlements();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign entitlement');
    }
  };

  const handleRevokeEntitlement = async (accountId, entitlementId) => {
    if (!window.confirm('Revoke this entitlement? A provisioning ticket will be created.')) return;
    try {
      await API.delete(`/app-accounts/assign-entitlement/${accountId}/${entitlementId}?create_ticket=true`);
      toast.success('Entitlement revoked');
      loadAccounts();
      loadEntitlements();
    } catch (err) {
      toast.error('Failed to revoke entitlement');
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <SpinnerGap size={32} weight="bold" className="animate-spin text-orange-500 mx-auto" />
          <p className="text-sm text-navy-500 mt-3">Loading application data...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="app-accounts-page" className="p-4 sm:p-6 md:p-8 space-y-6">
      {/* Breadcrumbs */}
      <Breadcrumbs items={[
        { label: 'Applications', path: '/applications' },
        { label: app?.name || 'Application' },
      ]} />

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
        <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-orange-100 flex items-center justify-center">
              <Cube size={24} weight="duotone" className="text-orange-600" />
            </div>
            <div>
              <h1 className="text-2xl font-black text-navy-900 tracking-tight">
                {app?.name}
              </h1>
              <p className="text-sm text-navy-500">Manage accounts and entitlements</p>
            </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={downloadTemplate}
            className="rounded-lg text-sm"
          >
            <Download size={16} className="mr-2" />
            Template
          </Button>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleImport}
            accept=".csv"
            className="hidden"
          />
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
            className="bg-orange-600 hover:bg-orange-700 text-white rounded-lg"
          >
            {importing ? (
              <><SpinnerGap size={16} className="mr-2 animate-spin" /> Importing...</>
            ) : (
              <><Upload size={16} className="mr-2" /> Import CSV</>
            )}
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <StatCard icon={User} label="Accounts" value={stats.account_count || 0} color="bg-blue-100 text-blue-600" />
          <StatCard icon={CheckCircle} label="Active" value={stats.active_accounts || 0} color="bg-emerald-100 text-emerald-600" />
          <StatCard icon={Key} label="Entitlements" value={stats.entitlement_count || 0} color="bg-amber-100 text-amber-600" />
          <StatCard icon={LinkIcon} label="Assignments" value={stats.assignment_count || 0} color="bg-purple-100 text-purple-600" />
          <StatCard icon={CheckCircle} label="Correlated" value={stats.correlated_accounts || 0} color="bg-emerald-100 text-emerald-600" />
          <StatCard icon={WarningCircle} label="Uncorrelated" value={stats.uncorrelated_accounts || 0} color="bg-orange-100 text-orange-600" />
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="accounts" className="space-y-4">
        <TabsList className="bg-slate-100 rounded-lg p-1">
          <TabsTrigger value="accounts" className="rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <User size={16} className="mr-2" />
            All Accounts ({accounts.length})
          </TabsTrigger>
          <TabsTrigger value="uncorrelated" className="rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <WarningCircle size={16} className="mr-2 text-orange-500" />
            Uncorrelated ({uncorrelatedAccounts.length})
          </TabsTrigger>
          <TabsTrigger value="entitlements" className="rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <Key size={16} className="mr-2" />
            Entitlements ({entitlements.length})
          </TabsTrigger>
        </TabsList>

        {/* Accounts Tab */}
        <TabsContent value="accounts" className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="relative max-w-sm">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-navy-400" />
              <Input
                placeholder="Search accounts..."
                value={accountSearch}
                onChange={(e) => setAccountSearch(e.target.value)}
                className="pl-9 h-9 rounded-lg border-slate-200"
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setShowAssignDialog(true)}
                className="rounded-lg text-sm"
              >
                <LinkIcon size={16} className="mr-2" />
                Assign Entitlement
              </Button>
              <Button
                onClick={() => {
                  setAccountForm({ account_id: '', email: '', display_name: '', status: 'active' });
                  setEditingAccount(null);
                  setShowAccountDialog(true);
                }}
                className="bg-slate-900 hover:bg-slate-800 text-white rounded-lg"
              >
                <Plus size={16} className="mr-2" />
                Add Account
              </Button>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
            <Table wrapperClassName="max-h-[calc(100vh-400px)]">
              <TableHeader>
                <TableRow className="bg-slate-50 shadow-[0_1px_3px_rgba(0,0,0,0.05)]">
                  <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500">Account</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500">Identity</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500">Entitlements</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500">Status</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {accounts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-12 text-sm text-navy-400">
                      No accounts found. Import a CSV or add accounts manually.
                    </TableCell>
                  </TableRow>
                ) : accounts.map((account) => (
                  <TableRow key={account.id} className="hover:bg-slate-50">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                          <User size={14} weight="duotone" className="text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium text-sm text-navy-900">{account.display_name}</p>
                          <p className="text-xs text-navy-400">{account.account_id} • {account.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {account.identity ? (
                        <div className="flex items-center gap-2">
                          <CheckCircle size={14} weight="fill" className="text-emerald-500" />
                          <span className="text-sm text-navy-700">{account.identity.display_name}</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <WarningCircle size={14} weight="fill" className="text-orange-500" />
                          <span className="text-sm text-navy-400">Not correlated</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleCorrelate(account.id)}
                            className="h-6 px-2 text-xs text-orange-600 hover:text-orange-700"
                          >
                            <LinkIcon size={12} className="mr-1" />
                            Correlate
                          </Button>
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className="bg-purple-100 text-purple-700 text-xs">
                        {account.entitlement_count} entitlements
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={account.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-navy-600'}>
                        {account.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/app-accounts/${appId}/account/${account.id}`)}
                          title="View Details"
                        >
                          <Eye size={14} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setAccountForm({
                              account_id: account.account_id,
                              email: account.email,
                              display_name: account.display_name,
                              status: account.status
                            });
                            setEditingAccount(account.id);
                            setShowAccountDialog(true);
                          }}
                        >
                          <PencilSimple size={14} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteAccount(account.id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash size={14} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* Uncorrelated Accounts Tab */}
        <TabsContent value="uncorrelated" className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="relative max-w-sm">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-navy-400" />
              <Input
                placeholder="Search uncorrelated accounts..."
                value={uncorrelatedSearch}
                onChange={(e) => setUncorrelatedSearch(e.target.value)}
                className="pl-9 h-9 rounded-lg border-slate-200"
              />
            </div>
            <Button
              variant="outline"
              onClick={async () => {
                // Bulk correlate all
                let correlated = 0;
                for (const acc of uncorrelatedAccounts) {
                  try {
                    const res = await API.post(`/app-accounts/${acc.id}/correlate`);
                    if (res.data.identity_id) correlated++;
                  } catch (e) { /* skip */ }
                }
                toast.success(`Correlated ${correlated} accounts`);
                loadAccounts();
                loadUncorrelatedAccounts();
                const statsRes = await API.get(`/applications/${appId}/stats`);
                setStats(statsRes.data);
              }}
              disabled={uncorrelatedAccounts.length === 0}
              className="rounded-lg text-sm"
            >
              <LinkIcon size={16} className="mr-2" />
              Correlate All
            </Button>
          </div>

          {/* Info Banner */}
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <WarningCircle size={20} weight="duotone" className="text-orange-600 mt-0.5" />
              <div>
                <h4 className="text-sm font-bold text-orange-900">Uncorrelated Accounts Need Attention</h4>
                <p className="text-xs text-orange-700 mt-1">
                  These accounts could not be automatically linked to IAG identities. 
                  Either no identity with a matching email exists, or the email is missing/incorrect.
                </p>
                <p className="text-xs text-orange-600 mt-2">
                  <strong>To fix:</strong> Update the account email to match an existing identity, or create a new identity with the matching email.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
            <Table wrapperClassName="max-h-[calc(100vh-400px)]">
              <TableHeader>
                <TableRow className="bg-slate-50 shadow-[0_1px_3px_rgba(0,0,0,0.05)]">
                  <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500">Account</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500">Email</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500">Entitlements</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500">Status</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {uncorrelatedAccounts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-12">
                      <div className="flex flex-col items-center gap-2">
                        <CheckCircle size={32} weight="duotone" className="text-emerald-500" />
                        <p className="text-sm text-navy-600 font-medium">All accounts are correlated!</p>
                        <p className="text-xs text-navy-400">No action needed.</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : uncorrelatedAccounts.map((account) => (
                  <TableRow key={account.id} className="hover:bg-orange-50/50">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center">
                          <User size={14} weight="duotone" className="text-orange-600" />
                        </div>
                        <div>
                          <p className="font-medium text-sm text-navy-900">{account.display_name}</p>
                          <p className="text-xs text-navy-400">{account.account_id}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <code className="text-xs bg-slate-100 px-2 py-0.5 rounded font-mono">
                          {account.email || 'No email'}
                        </code>
                        {!account.email && (
                          <Badge className="bg-red-100 text-red-700 text-[10px]">Missing</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className="bg-purple-100 text-purple-700 text-xs">
                        {account.entitlement_count} entitlements
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={account.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-navy-600'}>
                        {account.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleCorrelate(account.id)}
                          className="h-7 px-2 text-xs text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                        >
                          <LinkIcon size={12} className="mr-1" />
                          Retry Correlate
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setAccountForm({
                              account_id: account.account_id,
                              email: account.email,
                              display_name: account.display_name,
                              status: account.status
                            });
                            setEditingAccount(account.id);
                            setShowAccountDialog(true);
                          }}
                          title="Edit Account"
                        >
                          <PencilSimple size={14} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteAccount(account.id)}
                          className="text-red-500 hover:text-red-700"
                          title="Delete Account"
                        >
                          <Trash size={14} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* Entitlements Tab */}
        <TabsContent value="entitlements" className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="relative max-w-sm">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-navy-400" />
              <Input
                placeholder="Search entitlements..."
                value={entitlementSearch}
                onChange={(e) => setEntitlementSearch(e.target.value)}
                className="pl-9 h-9 rounded-lg border-slate-200"
              />
            </div>
            <Button
              onClick={() => {
                setEntitlementForm({ name: '', description: '', type: 'permission' });
                setEditingEntitlement(null);
                setShowEntitlementDialog(true);
              }}
              className="bg-slate-900 hover:bg-slate-800 text-white rounded-lg"
            >
              <Plus size={16} className="mr-2" />
              Add Entitlement
            </Button>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
            <Table wrapperClassName="max-h-[calc(100vh-400px)]">
              <TableHeader>
                <TableRow className="bg-slate-50 shadow-[0_1px_3px_rgba(0,0,0,0.05)]">
                  <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500">Entitlement</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500">Type</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500">Members</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500">Requestable</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entitlements.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-12 text-sm text-navy-400">
                      No entitlements found. Import a CSV or add entitlements manually.
                    </TableCell>
                  </TableRow>
                ) : entitlements.map((ent) => (
                  <TableRow key={ent.id} className="hover:bg-slate-50">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                          <Key size={14} weight="duotone" className="text-amber-600" />
                        </div>
                        <div>
                          <p className="font-medium text-sm text-navy-900">{ent.name}</p>
                          <p className="text-xs text-navy-400 truncate max-w-xs">{ent.description || 'No description'}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className="bg-slate-100 text-navy-600 text-xs capitalize">
                        {ent.type}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className="bg-blue-100 text-blue-700 text-xs">
                        {ent.member_count} members
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {ent.requestable ? (
                        <Badge className="bg-emerald-100 text-emerald-700 text-xs">Yes</Badge>
                      ) : (
                        <Badge className="bg-slate-100 text-navy-500 text-xs">No</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setEntitlementForm({
                              name: ent.name,
                              description: ent.description,
                              type: ent.type
                            });
                            setEditingEntitlement(ent.id);
                            setShowEntitlementDialog(true);
                          }}
                        >
                          <PencilSimple size={14} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteEntitlement(ent.id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash size={14} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center shrink-0">
            <FileText size={16} className="text-blue-600" />
          </div>
          <div>
            <h4 className="text-sm font-bold text-blue-900">CSV Import Format</h4>
            <p className="text-xs text-blue-700 mt-1">
              Upload a CSV with columns: <code className="bg-blue-100 px-1 rounded">account_id, email, display_name, status, entitlement_name, entitlement_description</code>
            </p>
            <p className="text-xs text-blue-600 mt-1">
              • Accounts are auto-correlated to IAG identities by email address<br/>
              • Each row creates an account-entitlement assignment<br/>
              • Provisioning tickets are created for all changes (grants & revokes)
            </p>
          </div>
        </div>
      </div>

      {/* Account Dialog */}
      <Dialog open={showAccountDialog} onOpenChange={setShowAccountDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingAccount ? 'Edit Account' : 'Add Account'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Account ID *</Label>
              <Input
                value={accountForm.account_id}
                onChange={(e) => setAccountForm({ ...accountForm, account_id: e.target.value })}
                className="mt-1 rounded-lg"
                placeholder="username or ID"
                disabled={!!editingAccount}
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Email *</Label>
              <Input
                value={accountForm.email}
                onChange={(e) => setAccountForm({ ...accountForm, email: e.target.value })}
                className="mt-1 rounded-lg"
                placeholder="user@company.com"
                disabled={!!editingAccount}
              />
              <p className="text-xs text-navy-400 mt-1">Used to correlate with IAG identities</p>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Display Name</Label>
              <Input
                value={accountForm.display_name}
                onChange={(e) => setAccountForm({ ...accountForm, display_name: e.target.value })}
                className="mt-1 rounded-lg"
                placeholder="John Doe"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Status</Label>
              <Select value={accountForm.status} onValueChange={(v) => setAccountForm({ ...accountForm, status: v })}>
                <SelectTrigger className="mt-1 rounded-lg">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="disabled">Disabled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAccountDialog(false)} className="rounded-lg">Cancel</Button>
            <Button onClick={handleSaveAccount} className="bg-orange-600 hover:bg-orange-700 text-white rounded-lg">
              {editingAccount ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Entitlement Dialog */}
      <Dialog open={showEntitlementDialog} onOpenChange={setShowEntitlementDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingEntitlement ? 'Edit Entitlement' : 'Add Entitlement'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Name *</Label>
              <Input
                value={entitlementForm.name}
                onChange={(e) => setEntitlementForm({ ...entitlementForm, name: e.target.value })}
                className="mt-1 rounded-lg"
                placeholder="Admin Access"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Description</Label>
              <Input
                value={entitlementForm.description}
                onChange={(e) => setEntitlementForm({ ...entitlementForm, description: e.target.value })}
                className="mt-1 rounded-lg"
                placeholder="Full administrative privileges"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Type</Label>
              <Select value={entitlementForm.type} onValueChange={(v) => setEntitlementForm({ ...entitlementForm, type: v })}>
                <SelectTrigger className="mt-1 rounded-lg">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="permission">Permission</SelectItem>
                  <SelectItem value="role">Role</SelectItem>
                  <SelectItem value="group">Group</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEntitlementDialog(false)} className="rounded-lg">Cancel</Button>
            <Button onClick={handleSaveEntitlement} className="bg-orange-600 hover:bg-orange-700 text-white rounded-lg">
              {editingEntitlement ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Entitlement Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Assign Entitlement to Account</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Select Account</Label>
              <Select value={selectedAccount || ''} onValueChange={setSelectedAccount}>
                <SelectTrigger className="mt-1 rounded-lg">
                  <SelectValue placeholder="Choose an account..." />
                </SelectTrigger>
                <SelectContent>
                  {accounts.map(acc => (
                    <SelectItem key={acc.id} value={acc.id}>
                      {acc.display_name} ({acc.account_id})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Select Entitlement</Label>
              <Select value={selectedEntitlement || ''} onValueChange={setSelectedEntitlement}>
                <SelectTrigger className="mt-1 rounded-lg">
                  <SelectValue placeholder="Choose an entitlement..." />
                </SelectTrigger>
                <SelectContent>
                  {entitlements.map(ent => (
                    <SelectItem key={ent.id} value={ent.id}>
                      {ent.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="flex items-center gap-2 text-amber-700">
                <Ticket size={16} weight="duotone" />
                <span className="text-xs font-medium">A provisioning ticket will be created for this assignment</span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignDialog(false)} className="rounded-lg">Cancel</Button>
            <Button
              onClick={handleAssignEntitlement}
              disabled={!selectedAccount || !selectedEntitlement}
              className="bg-orange-600 hover:bg-orange-700 text-white rounded-lg"
            >
              Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Results Dialog */}
      <Dialog open={showImportResults} onOpenChange={setShowImportResults}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Import Results</DialogTitle>
          </DialogHeader>
          {importResults && (
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-3">
                <ResultCard label="Accounts Created" value={importResults.accounts_created} color="emerald" />
                <ResultCard label="Accounts Updated" value={importResults.accounts_updated} color="blue" />
                <ResultCard label="Entitlements Created" value={importResults.entitlements_created} color="amber" />
                <ResultCard label="Assignments Created" value={importResults.assignments_created} color="purple" />
                <ResultCard label="Assignments Revoked" value={importResults.assignments_revoked} color="red" />
                <ResultCard label="Tickets Created" value={importResults.tickets_created} color="orange" />
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-emerald-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-emerald-700">{importResults.correlated}</p>
                  <p className="text-xs text-emerald-600">Correlated</p>
                </div>
                <div className="bg-orange-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-orange-700">{importResults.uncorrelated}</p>
                  <p className="text-xs text-orange-600">Uncorrelated</p>
                </div>
              </div>
              
              {importResults.errors?.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-xs font-semibold text-red-700 mb-2">Errors ({importResults.errors.length})</p>
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {importResults.errors.map((err, i) => (
                      <p key={`err-${i}-${err.slice(0,20)}`} className="text-xs text-red-600">{err}</p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setShowImportResults(false)} className="bg-slate-900 hover:bg-slate-800 text-white rounded-lg">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Helper Components
const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm">
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
        <Icon size={18} weight="duotone" />
      </div>
      <div>
        <p className="text-2xl font-black text-navy-900">{value}</p>
        <p className="text-xs text-navy-500 font-semibold uppercase tracking-wider">{label}</p>
      </div>
    </div>
  </div>
);

const ResultCard = ({ label, value, color }) => {
  const colors = {
    emerald: 'bg-emerald-50 text-emerald-700',
    blue: 'bg-blue-50 text-blue-700',
    amber: 'bg-amber-50 text-amber-700',
    purple: 'bg-purple-50 text-purple-700',
    red: 'bg-red-50 text-red-700',
    orange: 'bg-orange-50 text-orange-700',
  };
  
  return (
    <div className={`rounded-lg p-3 text-center ${colors[color]}`}>
      <p className="text-2xl font-bold">{value}</p>
      <p className="text-xs">{label}</p>
    </div>
  );
};
